'use client';

import { ItemAddonBehaviour, ItemTrait } from '@order-mage/integration-sdk';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { MAX_ADDON_NESTING_DEPTH } from '@/api/menu-list/max-addon-nesting-depth';
import type { StorefrontItemBody } from '@/api/menu-list/storefront-menu-snapshot.types';
import {
    getContainedSinglePickHash,
    getHashFromItemIdMap,
    getMemberHash,
    getPriceValueTranslation,
    useCartStore,
} from '@/entities/cart';
import { useStorefrontItemsIndex, useStorefrontSnapshot } from '@/entities/product/lib/useStorefrontMenu';
import { cn } from '@/shared/lib/cn';
import { getMainAsset } from '@/shared/lib/getMainAsset';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Currency } from '@/shared/model/Currency';
import { LocalizedString } from '@/shared/model/Language';
import { Icon } from '@/shared/ui/icon';
import { prefabsStore } from '@/shared/ui/modal/prefabs-store';
import {
    memberAmount,
    unmetAddonBounds,
    type UnmetAddonBound,
    isMemberOpeningMore,
    getMemberVariantChoices,
    nestedAddonSource,
    sectionReadyToAdvance,
    summaryAddonLineCharge,
    summaryAddonPackages,
    type StepSection,
    type StepSelections,
    type SummaryPackagePrice,
} from './addon-step.utils';
import { resolveProductDetailStepsMode } from './product-detail-steps-mode';
import { ProductDetailContext } from './provider';
import {
    listVisibleAddonSections,
    useVisibleAddonSections,
    type VisibleAddonSection,
} from './use-visible-addon-sections';

const JUMP_DELAY_MS = 350;

type StepsValue = {
    mode: 'column' | 'steps';
    step: number;
    reviewIndex: number;
    isReview: boolean;
    canContinue: boolean;
    /** Unmet bounds in screen order, each tagged with the step of the section it sits under. */
    blockers: Array<UnmetAddonBound & { step: number }>;
    pills: { id: string; label: string }[];
    goBack: () => void;
    goNext: () => void;
    goTo: (index: number) => void;
};

const StepsContext = createContext<StepsValue | null>(null);

export const useProductDetailSteps = (): StepsValue | null => useContext(StepsContext);

const toStepSection = (
    section: VisibleAddonSection,
    selections: StepSelections,
    itemsMap: ReturnType<typeof useStorefrontItemsIndex>,
    idToHash: Map<string, string>,
    memberVariants: Record<string, string | undefined> | undefined,
    getLocalizedValue: (value: LocalizedString) => string,
    getItemPrices: (hash: string) => unknown,
): StepSection => ({
    id: section.addonDef.id,
    label: getLocalizedValue(section.addonDef.name as LocalizedString) || section.addonDef.id,
    behaviour: section.addonDef.behaviour,
    minAmount: section.addonDef.minAmount,
    maxAmount: section.addonDef.maxAmount,
    autoJumpToNextSection: section.addonDef.autoJumpToNextSection,
    members: section.members.map((member) => {
        const hash = getMemberHash(member, idToHash) ?? '';
        const selected =
            memberAmount(
                { id: section.addonDef.id, behaviour: section.addonDef.behaviour, members: [] },
                selections,
                hash,
            ) > 0;
        const body = itemsMap[hash]?.body;
        const nested = nestedBody(hash, itemsMap, idToHash, memberVariants);
        const nestedCount = nested
            ? listVisibleAddonSections(nested, itemsMap, getLocalizedValue, getItemPrices).length
            : 0;
        return {
            hash,
            hasNested:
                selected &&
                isMemberOpeningMore(getMemberVariantChoices(body?.variants ?? []).length, nestedCount),
            contained: Number(member.amount?.value ?? 0),
        };
    }),
});

const nestedBody = (
    hash: string,
    itemsMap: ReturnType<typeof useStorefrontItemsIndex>,
    idToHash: Map<string, string>,
    memberVariants: Record<string, string | undefined> | undefined,
): StorefrontItemBody | undefined => {
    const body = itemsMap[hash]?.body;
    if (!body) return undefined;
    return nestedAddonSource(body, body.variants ?? [], memberVariants?.[body.id], (itemId) => {
        const variantHash = idToHash.get(itemId);
        return variantHash ? itemsMap[variantHash]?.body : undefined;
    });
};

export const ProductDetailStepsGate = ({
    layout,
    menuUsesSteps = true,
    /** Opens the guide on this step. Stories use it; the shop always starts at 0. */
    initialStep = 0,
    children,
}: {
    layout?: string;
    menuUsesSteps?: boolean;
    initialStep?: number;
    children?: ReactNode;
}) => {
    const { currentItem, formMethods } = useContext(ProductDetailContext);
    const getLocalizedValue = useGetLocalizedValue();
    const getItemPrices = useCartStore((s) => s.getItemPrices);
    const itemsMap = useStorefrontItemsIndex();
    const idToHash = useMemo(() => getHashFromItemIdMap(itemsMap), [itemsMap]);
    const visible = useVisibleAddonSections(currentItem);
    const selections = useWatch({ control: formMethods.control, name: 'addons' }) as
        | StepSelections
        | undefined;
    const memberVariants = useWatch({ control: formMethods.control, name: 'memberVariants' });
    const picked = useMemo(() => selections ?? {}, [selections]);

    const storedDetail = prefabsStore.getProductDetail();
    const storedMenuUsesSteps = prefabsStore.getPrefabs()?.ProductDetailModal?.menuUsesSteps;
    const mode: StepsValue['mode'] = resolveProductDetailStepsMode(
        storedDetail?.stepsMode,
        currentItem?.trait === ItemTrait.MENU,
        layout,
        storedMenuUsesSteps ?? menuUsesSteps,
    );

    const stepSections = useMemo(
        () =>
            visible.map((section) =>
                toStepSection(
                    section,
                    picked,
                    itemsMap,
                    idToHash,
                    memberVariants,
                    getLocalizedValue,
                    getItemPrices,
                ),
            ),
        [visible, picked, itemsMap, idToHash, memberVariants, getLocalizedValue, getItemPrices],
    );

    const reviewIndex = stepSections.length;
    const [step, setStep] = useState(() => Math.max(0, initialStep));
    const safeStep = Math.min(step, reviewIndex);

    const nestedOf = (section: StepSection, depth: number): StepSection[] => {
        // A nest renders only while depth + 1 is still on screen. The next catalog level stays hidden, so it must not block Continue.
        if (depth + 1 >= MAX_ADDON_NESTING_DEPTH) return [];
        return section.members.flatMap((member) => {
            if (memberAmount(section, picked, member.hash) <= 0) return [];
            const body = nestedBody(member.hash, itemsMap, idToHash, memberVariants);
            if (!body) return [];
            return listVisibleAddonSections(body, itemsMap, getLocalizedValue, getItemPrices).map((child) =>
                toStepSection(
                    child,
                    picked,
                    itemsMap,
                    idToHash,
                    memberVariants,
                    getLocalizedValue,
                    getItemPrices,
                ),
            );
        });
    };

    const current = stepSections[safeStep];
    const isReview = mode === 'steps' && safeStep >= reviewIndex;
    const stepBlockers = current
        ? unmetAddonBounds([current], picked, 0, nestedOf).map((issue) => ({ ...issue, step: safeStep }))
        : [];
    const allBlockers = stepSections.flatMap((section, index) =>
        unmetAddonBounds([section], picked, 0, nestedOf).map((issue) => ({ ...issue, step: index })),
    );
    const blockers = mode === 'steps' && !isReview ? stepBlockers : allBlockers;
    const canContinue = stepBlockers.length === 0;

    const addonSig = stepSections
        .map((section) => {
            const variants = section.members
                .map((member) => memberVariants?.[itemsMap[member.hash]?.body?.id ?? ''] ?? '')
                .join(',');
            const amounts = section.members
                .map((member) => `${member.hash}:${memberAmount(section, picked, member.hash)}`)
                .join('|');
            return `${amounts}@${variants}`;
        })
        .join('\n');
    const seen = useRef(false);
    const previousSig = useRef('');
    const advanced = useRef(new Set<number>());
    const jump = useRef({ picked, stepSections, reviewIndex, addonSig });
    const productVariant = useWatch({ control: formMethods.control, name: 'variant' });
    const variantKey = String(productVariant ?? '');
    const variantKeyRef = useRef(variantKey);
    const [trackedVariant, setTrackedVariant] = useState(variantKey);
    if (trackedVariant !== variantKey) {
        setTrackedVariant(variantKey);
        setStep(0);
    }

    useEffect(() => {
        jump.current = { picked, stepSections, reviewIndex, addonSig };
    }, [picked, stepSections, reviewIndex, addonSig]);

    useEffect(() => {
        const {
            picked: pickedNow,
            stepSections: sectionsNow,
            reviewIndex: reviewNow,
            addonSig: sig,
        } = jump.current;
        const variantChanged = variantKeyRef.current !== variantKey;
        variantKeyRef.current = variantKey;
        if (variantChanged) {
            advanced.current.clear();
            seen.current = true;
            previousSig.current = sig;
            return;
        }
        if (sectionsNow.length === 0) return;
        if (!seen.current) {
            seen.current = true;
            previousSig.current = sig;
            return;
        }
        if (previousSig.current === sig) return;
        const before = previousSig.current.split('\n');
        const after = sig.split('\n');
        previousSig.current = sig;
        const changed = after.findIndex((part, index) => before[index] !== part);
        const section = sectionsNow[changed];
        if (!section || advanced.current.has(changed) || !sectionReadyToAdvance(section, pickedNow)) return;
        if (mode === 'steps' && changed !== safeStep) return;
        const timer = window.setTimeout(() => {
            advanced.current.add(changed);
            if (mode === 'steps') {
                setStep((index) => Math.min(reviewNow, index + 1));
                return;
            }
            const next = sectionsNow[changed + 1];
            const target = next ? document.getElementById(`product-detail-addon-section-${next.id}`) : null;
            const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
            target?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }, JUMP_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [addonSig, mode, safeStep, variantKey]);

    const pills = [
        ...visible.map((section) => ({
            id: section.addonDef.id,
            label: getLocalizedValue(section.addonDef.name as LocalizedString) || section.addonDef.id,
        })),
        { id: 'summary', label: '' },
    ];

    const value: StepsValue = {
        mode,
        step: safeStep,
        reviewIndex,
        isReview,
        canContinue,
        blockers,
        pills,
        goBack: () => setStep((index) => Math.max(0, index - 1)),
        goNext: () => setStep((index) => Math.min(reviewIndex, index + 1)),
        goTo: (index) => {
            if (index <= safeStep) setStep(index);
        },
    };

    return <StepsContext.Provider value={value}>{children}</StepsContext.Provider>;
};

export const ProductDetailStepsProgress = () => {
    const steps = useProductDetailSteps();
    const t = useTranslations();
    const [emblaRef, emblaApi] = useEmblaCarousel({
        dragFree: true,
        align: 'center',
        containScroll: 'keepSnaps',
        watchDrag: (api) => api.canScrollPrev() || api.canScrollNext(),
    });
    const [draggable, setDraggable] = useState(false);
    useEffect(() => {
        if (!emblaApi) return;
        const sync = () => setDraggable(emblaApi.canScrollPrev() || emblaApi.canScrollNext());
        sync();
        emblaApi.on('reInit', sync).on('resize', sync);
        return () => {
            emblaApi.off('reInit', sync).off('resize', sync);
        };
    }, [emblaApi]);
    useEffect(() => {
        if (!emblaApi) return;
        const node = emblaApi.rootNode();
        const onWheel = (event: WheelEvent) => {
            if (!emblaApi.canScrollPrev() && !emblaApi.canScrollNext()) return;
            if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
            event.preventDefault();
            // Embla has no public pixel scroll. internalEngine is the trackpad path; a wheel plugin would be a new dependency.
            // The distance stays inside the track, otherwise both scroll flags go false and a later drag is ignored.
            const engine = emblaApi.internalEngine();
            const current = engine.target.get();
            const delta = engine.limit.constrain(current - event.deltaX) - current;
            if (delta === 0) return;
            engine.scrollBody.useFriction(0.3).useDuration(0);
            engine.scrollTo.distance(delta, false);
        };
        node.addEventListener('wheel', onWheel, { passive: false });
        return () => node.removeEventListener('wheel', onWheel);
    }, [emblaApi]);
    useEffect(() => {
        if (!emblaApi || steps?.mode !== 'steps') return;
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        emblaApi.scrollTo(steps.step, reduce);
    }, [emblaApi, steps?.mode, steps?.step]);
    if (steps?.mode !== 'steps') return null;
    const total = steps.reviewIndex + 1;
    const width = steps.reviewIndex === 0 ? 100 : (steps.step / steps.reviewIndex) * 100;
    return (
        <div
            className="product-detail-steps"
            data-testid="product-detail-steps"
            data-step={steps.step + 1}
            data-total={total}
        >
            <div
                className={cn(
                    'product-detail-steps__carousel',
                    draggable && 'product-detail-steps__carousel--draggable',
                )}
                data-testid="product-detail-steps-carousel"
                data-draggable={draggable ? 'true' : 'false'}
                ref={emblaRef}
            >
                <div className="product-detail-steps__pills" data-testid="product-detail-steps-pills">
                    {steps.pills.map((pill, index) => {
                        const locked = index > steps.step;
                        const state = index === steps.step ? 'active' : locked ? 'todo' : 'done';
                        const label = pill.id === 'summary' ? t('PRODUCT_DETAIL.SUMMARY') : pill.label;
                        return (
                            <button
                                key={pill.id}
                                type="button"
                                className={`product-detail-steps__pill product-detail-steps__pill--${state}`}
                                data-testid={
                                    pill.id === 'summary'
                                        ? 'product-detail-step-summary'
                                        : 'product-detail-step'
                                }
                                data-index={index + 1}
                                aria-disabled={locked || undefined}
                                aria-current={index === steps.step ? 'step' : undefined}
                                onClick={() => {
                                    if (!locked) steps.goTo(index);
                                }}
                            >
                                <span
                                    className="product-detail-steps__num"
                                    data-testid="product-detail-step-number"
                                >
                                    {index < steps.step ? '✓' : index + 1}
                                </span>
                                <span>{label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
            <div
                className="product-detail-steps__bar"
                data-testid="product-detail-steps-bar"
                aria-hidden="true"
            >
                <span className="product-detail-steps__bar-fill" style={{ width: `${width}%` }} />
            </div>
            <p className="product-detail-steps__count" data-testid="product-detail-step-count">
                {t('PRODUCT_DETAIL.STEP_OF', { current: steps.step + 1, total })}
            </p>
        </div>
    );
};

export const ProductDetailStepsSummary = () => {
    const steps = useProductDetailSteps();
    const t = useTranslations();
    const { currentItem, hash, formMethods } = useContext(ProductDetailContext);
    const getLocalizedValue = useGetLocalizedValue();
    const getItemPrices = useCartStore((s) => s.getItemPrices);
    const getItemFinalPrice = useCartStore((s) => s.getItemFinalPrice);
    const itemsMap = useStorefrontItemsIndex();
    const snapshot = useStorefrontSnapshot();
    const idToHash = useMemo(() => getHashFromItemIdMap(itemsMap), [itemsMap]);
    const visible = useVisibleAddonSections(currentItem);
    const selections = (useWatch({ control: formMethods.control, name: 'addons' }) ?? {}) as StepSelections;
    const memberVariants = useWatch({ control: formMethods.control, name: 'memberVariants' });
    const variant = useWatch({ control: formMethods.control, name: 'variant' });
    const quantity = useWatch({ control: formMethods.control, name: 'quantity' });
    if (!steps?.isReview) return null;

    const plusPrice = (amount: number, currency?: string) => {
        if (!(amount > 0) || !currency) return null;
        return `+${getPriceValueTranslation({ amount, currency: currency as Currency, t })}`;
    };
    const pricedPackages = (hashKey: string | undefined): SummaryPackagePrice[] => {
        if (!hashKey) return [];
        const prices = getItemPrices(hashKey) as {
            packagePrices?: Array<{ packageId?: string; priceValue: { amount: string; currency: string } }>;
        } | null;
        return (prices?.packagePrices ?? []).flatMap((pkg) => {
            if (!pkg.packageId) return [];
            const amount = Number(pkg.priceValue.amount || 0);
            return [
                {
                    id: pkg.packageId,
                    amount: Number.isFinite(amount) ? amount : 0,
                    currency: pkg.priceValue.currency,
                },
            ];
        });
    };
    const namedPackages = (rows: SummaryPackagePrice[]) =>
        rows.flatMap((pkg) => {
            const localized = snapshot?.packageItems[pkg.id]?.name;
            const name = localized ? getLocalizedValue(localized) : '';
            if (!name) return [];
            return [{ ...pkg, name }];
        });

    const renderRows = (section: VisibleAddonSection, depth: number): ReactNode => {
        if (depth >= MAX_ADDON_NESTING_DEPTH) return null;
        const step = toStepSection(
            section,
            selections,
            itemsMap,
            idToHash,
            memberVariants,
            getLocalizedValue,
            getItemPrices,
        );
        const rows = section.members.flatMap((member) => {
            const hashKey = getMemberHash(member, idToHash) ?? '';
            if (!hashKey) return [];
            const qty = memberAmount(step, selections, hashKey);
            const contained =
                section.addonDef.behaviour === ItemAddonBehaviour.SINGLE_PICK
                    ? hashKey === getContainedSinglePickHash(section.members, idToHash)
                        ? 1
                        : 0
                    : Number(member.amount?.value ?? 0);
            const removed = contained > 0 && qty < contained;
            if (qty <= 0 && !removed) return [];
            const item = itemsMap[hashKey];
            const name = getLocalizedValue(item?.body.name as LocalizedString) ?? '';
            const variantRows = item?.body.variants ?? [];
            const chosen = memberVariants?.[item?.body.id ?? ''];
            const active = variantRows.find((row) => row.id === chosen);
            const variantHash = active && !active.isDefault ? idToHash.get(active.itemId) : undefined;
            const defaultRow = variantRows.find((row) => row.isDefault);
            const defaultHash = defaultRow ? idToHash.get(defaultRow.itemId) : undefined;
            const variantName = variantHash
                ? getLocalizedValue(itemsMap[variantHash]?.body.name as LocalizedString)
                : '';
            const label = variantName ? `${name} - ${variantName}` : name;
            const memberPrices = getItemPrices(hashKey);
            const variantPrices = variantHash ? getItemPrices(variantHash) : null;
            const defaultPrices = variantHash && defaultHash ? getItemPrices(defaultHash) : null;
            const memberUnit = Number(memberPrices?.itemPrice?.amount ?? 0);
            const charge = {
                amount: summaryAddonLineCharge({
                    quantity: qty,
                    contained,
                    removed,
                    memberUnit,
                    variant: variantHash
                        ? {
                              itemUnit: Number(variantPrices?.itemPrice?.amount ?? 0),
                              defaultItemUnit: Number(defaultPrices?.itemPrice?.amount ?? 0),
                          }
                        : undefined,
                }),
                currency: memberPrices?.itemPrice?.currency ?? variantPrices?.itemPrice?.currency,
            };
            const packages = namedPackages(
                summaryAddonPackages({
                    quantity: qty,
                    contained,
                    removed,
                    memberPackages: pricedPackages(hashKey),
                    variantPackages: variantHash ? pricedPackages(variantHash) : null,
                    defaultVariantPackages: variantHash ? pricedPackages(defaultHash) : [],
                }),
            );
            const nestedSource =
                removed || qty <= 0 ? undefined : nestedBody(hashKey, itemsMap, idToHash, memberVariants);
            const nested = nestedSource
                ? listVisibleAddonSections(nestedSource, itemsMap, getLocalizedValue, getItemPrices)
                : [];
            const count = removed ? contained - qty : qty;
            const signed = `${removed ? '-' : '+'}${count > 1 ? `${count}x ` : ' '}${label}`;
            const price = plusPrice(charge.amount, charge.currency);
            return [
                {
                    hashKey,
                    removed,
                    label,
                    count,
                    signed,
                    price,
                    packages,
                    nested,
                    photo: getMainAsset(item?.body.assets ?? [])?.fullUrl,
                },
            ];
        });
        const ordered = [...rows.filter((row) => !row.removed), ...rows.filter((row) => row.removed)];
        return ordered.map((row) => (
            <div className="product-detail-steps__summary-node" key={`${section.addonDef.id}-${row.hashKey}`}>
                {depth > 0 ? (
                    <p
                        className="product-detail-steps__summary-line"
                        data-testid="product-detail-summary-row"
                    >
                        <span
                            className={
                                row.removed
                                    ? 'product-detail-steps__summary-removed'
                                    : 'product-detail-steps__summary-added'
                            }
                        >
                            {row.removed ? '-' : '+'}
                        </span>
                        {row.photo ? (
                            <Image
                                src={row.photo}
                                alt=""
                                width={32}
                                height={32}
                                className="product-detail-steps__thumb"
                            />
                        ) : null}
                        <span
                            className={
                                row.removed
                                    ? 'product-detail-steps__summary-removed'
                                    : 'product-detail-steps__summary-added'
                            }
                        >
                            {`${row.count > 1 ? `${row.count}x ` : ''}${row.label}`}
                        </span>
                        {(row.price || !row.removed) && (
                            <span
                                className={cn(
                                    'product-detail-steps__summary-price',
                                    row.price
                                        ? 'product-detail-steps__summary-price--added'
                                        : 'product-detail-steps__summary-price--included',
                                )}
                            >
                                {row.price ?? t('PRODUCT_DETAIL.IN_PRICE')}
                            </span>
                        )}
                    </p>
                ) : (
                    <div
                        className="product-detail-steps__summary-row"
                        data-testid="product-detail-summary-row"
                    >
                        {row.photo ? (
                            <Image
                                src={row.photo}
                                alt=""
                                width={32}
                                height={32}
                                className="product-detail-steps__thumb"
                            />
                        ) : null}
                        <span
                            className={cn(
                                'product-detail-steps__summary-name',
                                row.removed && 'product-detail-steps__summary-removed',
                            )}
                        >
                            {row.removed
                                ? row.signed
                                : `${row.count > 1 ? `${row.count}x ` : ''}${row.label}`}
                        </span>
                        {(row.price || !row.removed) && (
                            <span
                                className={cn(
                                    'product-detail-steps__summary-price',
                                    !row.price && 'product-detail-steps__summary-price--included',
                                )}
                            >
                                {row.price ?? t('PRODUCT_DETAIL.IN_PRICE')}
                            </span>
                        )}
                    </div>
                )}
                {row.packages.length > 0 || row.nested.length > 0 ? (
                    <div className="product-detail-steps__summary-branch">
                        {row.packages.map((pkg) => {
                            const packagePrice = plusPrice(pkg.amount, pkg.currency);
                            return (
                                <div className="product-detail-steps__summary-node" key={pkg.id}>
                                    <p className="product-detail-steps__summary-line">
                                        <Icon
                                            name="package"
                                            size={14}
                                            className="product-detail-steps__summary-package-icon"
                                        />
                                        <span className="product-detail-steps__summary-package">
                                            {pkg.name}
                                        </span>
                                        <span className="product-detail-steps__summary-price product-detail-steps__summary-price--package">
                                            {packagePrice ?? t('PRODUCT_DETAIL.IN_PRICE')}
                                        </span>
                                    </p>
                                </div>
                            );
                        })}
                        {row.nested.map((child) => renderRows(child, depth + 1))}
                    </div>
                ) : null}
            </div>
        ));
    };

    const totalPrice = getItemFinalPrice(
        {
            variant,
            addons: selections as Record<string, string | Record<string, number | boolean>>,
            memberVariants,
        },
        hash,
        currentItem?.addons,
    );
    const amount = parseFloat(String(totalPrice?.amount || '0')) * (quantity ?? 1);

    return (
        <div className="product-detail-steps__summary" data-testid="product-detail-summary">
            <h3 className="product-detail-steps__summary-title">{t('PRODUCT_DETAIL.YOUR_MENU_IS_READY')}</h3>
            {visible.map((section) => {
                const rows = renderRows(section, 0);
                const hasRows = Array.isArray(rows) && rows.some(Boolean);
                return (
                    <div key={section.addonDef.id} className="product-detail-steps__summary-card">
                        <p className="product-detail-steps__summary-head">
                            {getLocalizedValue(section.addonDef.name as LocalizedString)}
                        </p>
                        {hasRows ? (
                            rows
                        ) : (
                            <p className="product-detail-steps__summary-empty">
                                {t('PRODUCT_DETAIL.NO_SELECTION')}
                            </p>
                        )}
                    </div>
                );
            })}
            <div className="product-detail-steps__summary-total">
                <span>{t('PRODUCT_DETAIL.TOTAL')}</span>
                <strong data-testid="product-detail-summary-total">
                    {totalPrice?.currency
                        ? getPriceValueTranslation({
                              amount,
                              currency: totalPrice.currency as Currency,
                              t,
                          })
                        : null}
                </strong>
            </div>
        </div>
    );
};
